import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useLang } from "@/hooks/use-lang";
import { Lang, i18n } from "@/i18n-config";

// 定義 API 回傳資料型別
export const financialMetricsComparisonSchema = z.object({
  data: z.array(
    z.object({
      indicator: z.string().nullish(),
      value: z.number().nullish(),
      pr: z.number().nullish(),
    })
  ),
  message: z.string(),
});

export type FinancialMetricsComparison = z.infer<
  typeof financialMetricsComparisonSchema
>;

export type FinancialMetricsComparisonParams = {
  symbol: string;
  lang: Lang;
};

export const getFinancialMetricsComparison = async ({
  symbol,
  lang,
}: FinancialMetricsComparisonParams): Promise<FinancialMetricsComparison> => {
  const endpoint = `/financial-metrics-comparison/${symbol}`;

  if (!symbol || !lang) {
    throw new Error(`GET: ${endpoint}\n Missing required params`);
  }
  if (!i18n.locales.includes(lang)) {
    throw new Error(`GET: ${endpoint}\n Invalid lang: ${lang}`);
  }

  const headers = new Headers({ "Accept-Language": lang });
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers,
  });

  if (!res.ok)
    throw new Error(
      `Failed to fetch financial metrics comparison for ${symbol}:\nStatus: ${
        res.status
      }\nResponse Text: ${await res.text()}`
    );

  return financialMetricsComparisonSchema.parse(await res.json());
};

export const useFinancialMetricsComparison = (symbol: string) => {
  const { lang } = useLang();
  return useQuery(
    ["financial-metrics-comparison", symbol, lang],
    () => getFinancialMetricsComparison({ symbol, lang }),
    { enabled: !!symbol }
  );
};
